import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

import tokens from "../tokens.json";

/**
 * 🔴 TAILWIND-MERGE DOES NOT KNOW THIS PROJECT'S FONT-SIZE SCALE UNLESS IT IS
 * TOLD, AND THE FAILURE IS A SILENT DELETION.
 *
 * `tailwind.config.cjs` adds this project's scale under `theme.extend`, so the
 * sizes here are `text-label` … `text-display` ALONGSIDE Tailwind's own
 * `text-sm`/`text-base`. tailwind-merge ships with the default scale only, so
 * it does not recognise `text-caption` as a size — and its fallback for an
 * unrecognised `text-*` is to classify it as a text COLOUR.
 *
 * (An earlier version of this comment said the config REPLACES `fontSize`. It
 * does not; it extends. The bug and the fix are unaffected — tailwind-merge
 * never knew the custom names either way — but the sentence was wrong, and a
 * wrong sentence in the file that documents a silent-failure bug is the last
 * place to leave one.)
 *
 * That puts every size in the same conflict group as every `text-onyx`,
 * `text-gray-mid` and `text-status-*` tone, and the later class wins:
 *
 *     twMerge("text-caption text-status-success")  ->  "text-status-success"   // size deleted
 *     twMerge("text-onyx text-label")              ->  "text-label"            // COLOUR deleted
 *
 * The class is in the source and absent from the DOM, which is why reading a
 * `cva` base tells you nothing about what reaches the browser — the merge sits
 * between them. It ran in BOTH directions: 131 call sites lost a size, and
 * `Button` lost its colour on all seven variants at every text-bearing size,
 * so `destructive` shipped as inherited dark text on red rather than the
 * white-on-red it is written to be.
 *
 * THE SCALE IS DERIVED FROM `tokens.json`, NOT RETYPED. A hand-written list is
 * a second place the scale is written down, and the first thing to go stale
 * when a token is added — which is the shape of the bug this repairs.
 *
 * `extend` rather than `override`: it ADDS these to the built-in group instead
 * of replacing it, so nothing that already merged correctly changes behaviour.
 *
 * KNOWN AND DELIBERATELY NOT FIXED HERE. The other custom scales
 * (`letterSpacing`, `radii`, `shadows`, `motion.duration`, `spacing`,
 * `maxWidth`) are also unrecognised, but they fail differently and far more
 * mildly: tailwind-merge puts them in NO group, so BOTH classes survive and
 * ordinary CSS source order decides. Nothing is deleted and no symptom has
 * been reported. Registering them would change rendered output at call sites
 * nothing has measured, which is precisely the unmeasured churn this change
 * exists to avoid. Measure first, in its own change.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [{ text: Object.keys(tokens.typography.fontSize) }],
    },
  },
});

/**
 * cn — combines clsx + tailwind-merge.
 * Use this for every className composition in shadcn/ui-style components
 * so conflicting Tailwind classes resolve correctly.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
